// store/pallets.redux.ts
import { AnyAction } from "redux"

export type MHCodeStateType = {
  id?: string
  responseId?: string
  mhCode: string
  boxes: number
  variety?: string
  commodityName?: string
}

export type RejectionDetailType = {
  gatePassId: string
  gatePassName: string
  rejectedWeight: number
  rejectedBoxes?: number
  remark?: string
}

export type GatePassStateType = {
  id?: string
  responseId?: string
  gatePass: string
  boxes: number
  weight: number
  farmerName: string
  variety?: string
  rejectedWeight?: number
  rejectedQuantity?: number
}

export type LotCodeType = {
  id?: string
  date: string
  lotCode: string
  gatePass: string
  productionCreatedDate?: string
}

export type PalletStateType = {
  id?: string
  sequenceNumber: number
  palletNumber: string
  mhCodes: MHCodeStateType[]
  gatePasses: GatePassStateType[]
  boxCount: number
  weightCount: number
  isEuro: boolean
  remark?: string
  size?: string
  error?: string
  date?: string
  lotCode?: LotCodeType[]
}

export type ProductionStateType = {
  id?: string
  pallets: PalletStateType[]
}

export interface ProductionState {
  production: ProductionStateType
  loading: boolean
  error?: string
}

// Initial state
const initialState: ProductionState = {
  production: {
    pallets: [],
  },
  loading: false,
  error: undefined,
}

// Reducer
export default function productionReducer(
  state = initialState,
  action: AnyAction
): ProductionState {
  switch (action.type) {
    case "PRODUCTION/SET":
      return { ...state, production: action.payload }

    case "PRODUCTION/LOADING":
      return { ...state, loading: action.payload }

    case "PRODUCTION/ERROR":
      return { ...state, error: action.payload, loading: false }

    // --- Pallet actions ---
    case "PRODUCTION/PALLET/ADD":
      return {
        ...state,
        production: {
          ...state.production,
          pallets: [...state.production.pallets, action.payload],
        },
      }

    case "PRODUCTION/PALLET/UPDATE":
      const { pallet, index } = action.payload
      return {
        ...state,
        production: {
          ...state.production,
          pallets: state.production.pallets.map((p, i) =>
            index !== undefined
              ? i === index
                ? { ...p, ...pallet }
                : p
              : p.id === pallet.id
              ? { ...p, ...pallet }
              : p
          ),
        },
      }

    case "PRODUCTION/PALLET/REMOVE":
      return {
        ...state,
        production: {
          ...state.production,
          pallets: state.production.pallets.filter(
            (_, i) => i !== action.payload
          ),
        },
      }
    case "PRODUCTION/PALLETS/RESET_MH":
      return {
        ...state,
        production: {
          ...state.production,
          pallets: state.production.pallets.map(p => ({
            ...p,
            mhCodes: [],
          })),
        },
      }

    case "PRODUCTION/PALLETS/RESET_GP":
      return {
        ...state,
        production: {
          ...state.production,
          pallets: state.production.pallets.map(p => ({
            ...p,
            gatePasses: [],
            boxCount: 0,
            weightCount: 0,
          })),
        },
      }

    default:
      return state
  }
}

// Action creators
export const setProduction = (production: ProductionStateType) => ({
  type: "PRODUCTION/SET",
  payload: production,
})

export const setProductionLoading = (loading: boolean) => ({
  type: "PRODUCTION/LOADING",
  payload: loading,
})

export const setProductionError = (error: string) => ({
  type: "PRODUCTION/ERROR",
  payload: error,
})

export const updatePallet = (pallet: PalletStateType, index?: number) => ({
  type: "PRODUCTION/PALLET/UPDATE",
  payload: { pallet, index },
})

// Add a new pallet
export const addPallet = (pallet: PalletStateType) => ({
  type: "PRODUCTION/PALLET/ADD",
  payload: pallet,
})

// Remove a pallet by index
export const removePallet = (index: number) => ({
  type: "PRODUCTION/PALLET/REMOVE",
  payload: index,
})

export const resetAllMhCodes = () => ({
  type: "PRODUCTION/PALLETS/RESET_MH",
})

export const resetAllGatePasses = () => ({
  type: "PRODUCTION/PALLETS/RESET_GP",
})
